'use client';

import Script from 'next/script';

declare global {
  interface Window {
    silktideConsentManager?: {
      init: (config: any) => void;
      update: (config: any) => void;
      getInstance: () => any;
      resetConsent: () => void;
    };
    postbrixUpdateConsent?: (type: 'analytics' | 'marketing', granted: boolean) => void;
  }
}

/**
 * Silktide Consent Manager Script.
 * Ensure this script is loaded asynchronously and only once.
 *
 * NOTE: The consent manager CSS is statically bundled in the public layout
 * via `import './css/consent-manager.min.css'` — no dynamic <link> injection
 * needed here. The previous useEffect that injected /silktide/consent-manager.min.css
 * was creating a redundant second stylesheet request on every page load.
 */
export function SilktideScript() {


  return (
    <Script
      id="silktide-consent-manager-js"
      src="/silktide/consent-manager.min.js"
      strategy="lazyOnload"
        onReady={() => {
          if (
            window.silktideConsentManager &&
            typeof window.silktideConsentManager.init === 'function'
          ) {
            window.silktideConsentManager.init({
              backdrop: {
                show: false,
              },
              icon: {
                position: 'bottomRight',
              },
              prompt: {
                position: 'bottomRight',
              },
              consentTypes: [
                {
                  id: 'essential',
                  label: 'Essential',
                  description:
                    '<p>These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in and setting your privacy preferences.</p>',
                  required: true,
                  onAccept: function () {},
                },
                {
                  id: 'analytics',
                  label: 'Analytics',
                  description:
                    '<p>These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site.</p>',
                  defaultValue: false,
                  gtag: 'analytics_storage',
                  onAccept: function () {
                    if (typeof window.postbrixUpdateConsent === 'function') {
                      window.postbrixUpdateConsent('analytics', true);
                    }
                  },
                  onReject: function () {
                    if (typeof window.postbrixUpdateConsent === 'function') {
                      window.postbrixUpdateConsent('analytics', false);
                    }
                  },
                },
                {
                  id: 'marketing',
                  label: 'Marketing',
                  description:
                    '<p>These cookies are used by us and our advertising partners to show you relevant ads on this site and elsewhere, and to measure how those campaigns perform.</p>',
                  gtag: ['ad_storage', 'ad_user_data', 'ad_personalization'],
                  onAccept: function () {
                    if (typeof window.postbrixUpdateConsent === 'function') {
                      window.postbrixUpdateConsent('marketing', true);
                    }
                  },
                  onReject: function () {
                    if (typeof window.postbrixUpdateConsent === 'function') {
                      window.postbrixUpdateConsent('marketing', false);
                    }
                  },
                },
              ],
              text: {
                prompt: {
                  description:
                    '<p>We use cookies on our site to enhance your user experience, provide personalized content, and analyze our traffic.</p>',
                  acceptAllButtonText: 'Accept all',
                  acceptAllButtonAccessibleLabel: 'Accept all',
                  rejectNonEssentialButtonText: 'Reject non-essential',
                  rejectNonEssentialButtonAccessibleLabel: 'Reject non-essential',
                  preferencesButtonText: 'Preferences',
                  preferencesButtonAccessibleLabel: 'Preferences',
                },
                preferences: {
                  title: 'Customize your cookie preferences',
                  description:
                    '<p>We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.</p>',
                  saveButtonText: 'Save and close',
                  saveButtonAccessibleLabel: 'Save your cookie preferences',
                  creditLinkText: '',
                  creditLinkAccessibleLabel: '',
                },
              },
            });

            // Ensure Silktide banner does not hijack the page's single H1 heading
            const demoteCookieH1 = () => {
              const h1s = document.querySelectorAll(
                '#stcm-modal h1, #stcm-wrapper h1, #silktide-consent-manager h1, .silktide-consent-manager h1, [class*="silktide"] h1'
              );
              h1s.forEach((h1) => {
                const h2 = document.createElement('h2');
                for (const attr of Array.from(h1.attributes)) {
                  h2.setAttribute(attr.name, attr.value);
                }
                h2.innerHTML = h1.innerHTML;
                h1.parentNode?.replaceChild(h2, h1);
              });
            };
            demoteCookieH1();
            const observer = new MutationObserver(demoteCookieH1);
            observer.observe(document.body, { childList: true, subtree: true });
          }
        }}
      />
  );
}
